import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, RefreshCw, Eye, EyeOff, Percent, Wallet, MessageCircle, Phone as PhoneIcon, KeyRound, Search, MapPin, Loader2, Map as MapIcon, Ticket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AgentsMiniMap from '../components/AgentsMiniMap';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '', phone: '', whatsapp: '', address: '',
  latitude: '', longitude: '',
  commission_type: 'percent', commission_value: 10,
  is_active: true, notes: '',
  login_username: '', login_password: '',
};

const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

export default function VoucherAgents() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [cascadeDelete, setCascadeDelete] = useState(false);
  const [gpsFetching, setGpsFetching] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const fetchGps = () => {
    if (!navigator.geolocation) {
      toast.error('Browser tidak mendukung geolocation');
      return;
    }
    setGpsFetching(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(7),
          longitude: pos.coords.longitude.toFixed(7),
        }));
        toast.success('Lokasi berhasil diambil');
        setGpsFetching(false);
      },
      (err) => {
        toast.error('Gagal ambil lokasi: ' + err.message);
        setGpsFetching(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('limit', '200');
      const res = await axios.get(`${API_URL}/api/voucher-agents?${params}`, auth);
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memuat data agen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchAgents(); /* eslint-disable-next-line */ }, [token]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      name: a.name || '', phone: a.phone || '', whatsapp: a.whatsapp || '',
      address: a.address || '',
      latitude: a.latitude != null ? String(a.latitude) : '',
      longitude: a.longitude != null ? String(a.longitude) : '',
      commission_type: a.commission_type || 'percent',
      commission_value: Number(a.commission_value || 0),
      is_active: !!a.is_active,
      notes: a.notes || '',
      login_username: a.login_username || '',
      login_password: '',
    });
    setShowModal(true);
  };

  const submit = async () => {
    if (!form.name?.trim()) return toast.error('Nama agen wajib diisi');
    if (!editingId) {
      if (!form.login_username?.trim() || form.login_username.length < 3) return toast.error('Username login minimal 3 karakter');
      if (!form.login_password || form.login_password.length < 6) return toast.error('Password minimal 6 karakter');
    }
    if (form.commission_type === 'percent' && (form.commission_value < 0 || form.commission_value > 100))
      return toast.error('Komisi persen harus 0-100');
    if (form.commission_type === 'nominal' && form.commission_value < 0)
      return toast.error('Komisi nominal harus >= 0');

    setSaving(true);
    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.login_password) delete payload.login_password;
        delete payload.login_username;
        await axios.put(`${API_URL}/api/voucher-agents/${editingId}`, payload, auth);
        toast.success('Agen berhasil diperbarui');
      } else {
        const payload = { ...form };
        payload.latitude = payload.latitude ? parseFloat(payload.latitude) : null;
        payload.longitude = payload.longitude ? parseFloat(payload.longitude) : null;
        const res = await axios.post(`${API_URL}/api/voucher-agents`, payload, auth);
        toast.success(`Agen "${form.name}" dibuat. Login: ${res.data.data.login_username}`);
      }
      setShowModal(false);
      fetchAgents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan agen');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const url = `${API_URL}/api/voucher-agents/${deleteConfirm.id}${cascadeDelete ? '?cascade=true' : ''}`;
      const res = await axios.delete(url, auth);
      toast.success(res.data.message || 'Agen berhasil dihapus');
      setDeleteConfirm(null);
      setCascadeDelete(false);
      fetchAgents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus');
    }
  };

  return (
    <div className="space-y-4 p-3 sm:p-6" data-testid="voucher-agents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
            Agen Voucher
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Kelola daftar penjual voucher hotspot &amp; komisi
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMap(v => !v)}
            className="h-9"
            data-testid="toggle-map-btn"
          >
            <MapIcon className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">{showMap ? 'Sembunyikan Peta' : 'Peta Agen'}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAgents}
            disabled={loading}
            className="h-9"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''} sm:mr-1`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button
            onClick={openCreate}
            size="sm"
            className="h-9 bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="create-agent-btn"
          >
            <Plus className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Tambah Agen</span>
            <span className="sm:hidden">Tambah</span>
          </Button>
        </div>
      </div>

      {showMap && (
        <div className="rounded-lg border border-purple-200 dark:border-purple-800/40 bg-purple-50/30 dark:bg-purple-950/20 p-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-purple-700 dark:text-purple-300 mb-2">
            <MapIcon className="w-4 h-4" /> Peta Sebaran Agen Voucher
          </div>
          <AgentsMiniMap agents={rows} height={360} />
        </div>
      )}

      {/* Search */}
      <Card className="shadow-sm">
        <CardContent className="p-3">
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cari nama / phone / whatsapp..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchAgents()}
                className="pl-8 h-9"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" size="sm" className="h-9" onClick={fetchAgents}>Cari</Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3 shadow-sm">
          <p className="text-xs text-slate-500 dark:text-slate-400">Total Agen</p>
          <p className="text-xl font-bold text-slate-900 dark:text-white">{total}</p>
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3 shadow-sm">
          <p className="text-xs text-slate-500 dark:text-slate-400">Aktif</p>
          <p className="text-xl font-bold text-emerald-500">{rows.filter(r => r.is_active).length}</p>
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3 shadow-sm">
          <p className="text-xs text-slate-500 dark:text-slate-400">Voucher Terjual</p>
          <p className="text-xl font-bold text-blue-500">{rows.reduce((s, r) => s + (r.voucher_sold || 0), 0)}</p>
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3 shadow-sm">
          <p className="text-xs text-slate-500 dark:text-slate-400">Total Komisi</p>
          <p className="text-lg sm:text-xl font-bold text-emerald-600">
            {fmtIDR(rows.reduce((s, r) => s + Number(r.commission_total || 0), 0))}
          </p>
        </div>
      </div>

      {/* Table (desktop) */}
      <Card className="shadow-sm hidden md:block">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Daftar Agen ({total})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200 dark:from-slate-800 dark:to-slate-800 dark:border-slate-700">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">Nama</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">Kontak</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">Login</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">Cabang</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">Komisi</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-700 dark:text-slate-200">Voucher</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-700 dark:text-slate-200">Komisi Total</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-700 dark:text-slate-200">Status</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-700 dark:text-slate-200">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-slate-400 py-8">Memuat...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-slate-400 py-10">
                    Belum ada agen. Klik <b>Tambah Agen</b> untuk membuat.
                  </td></tr>
                ) : rows.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`row-agent-${a.id}`}>
                    <td className="px-3 py-2.5 font-medium text-slate-900 dark:text-white">{a.name}</td>
                    <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300 text-xs">
                      {a.phone && <div className="flex items-center gap-1"><PhoneIcon className="w-3 h-3" /> {a.phone}</div>}
                      {a.whatsapp && <div className="flex items-center gap-1 text-emerald-600 mt-0.5"><MessageCircle className="w-3 h-3" /> {a.whatsapp}</div>}
                      {!a.phone && !a.whatsapp && <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300 text-xs font-mono">{a.login_username || '-'}</td>
                    <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300 text-xs">{a.branch_name || '-'}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {a.commission_type === 'percent' ? (
                        <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 font-semibold">
                          <Percent className="w-3 h-3" /> {a.commission_value}%
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
                          <Wallet className="w-3 h-3" /> {fmtIDR(a.commission_value)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className="text-slate-900 dark:text-white font-semibold">{a.voucher_sold || 0}</span>
                      <span className="text-slate-400 text-xs"> / {a.voucher_total || 0}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-emerald-600 dark:text-emerald-400 font-semibold text-sm">
                      {fmtIDR(a.commission_total)}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {a.is_active ? (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium dark:bg-emerald-900/30 dark:text-emerald-400">Aktif</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium dark:bg-red-900/30 dark:text-red-400">Nonaktif</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => navigate(`/app/vouchers?agent_id=${a.id}`)}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md dark:text-emerald-400 dark:hover:bg-emerald-900/40"
                          title={`Lihat voucher milik ${a.name}`}
                          data-testid={`view-vouchers-${a.id}`}
                        >
                          <Ticket className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openEdit(a)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md dark:text-blue-400 dark:hover:bg-blue-900/40"
                          title="Edit"
                          data-testid={`edit-${a.id}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(a)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-md dark:text-red-400 dark:hover:bg-red-900/40"
                          title="Hapus"
                          data-testid={`delete-${a.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {loading && rows.length === 0 && (
          <Card className="shadow-sm"><CardContent className="text-center text-slate-400 py-8">Memuat...</CardContent></Card>
        )}
        {!loading && rows.length === 0 && (
          <Card className="shadow-sm"><CardContent className="text-center text-slate-400 py-10">
            Belum ada agen. Klik <b>Tambah Agen</b>.
          </CardContent></Card>
        )}
        {rows.map((a) => (
          <Card key={a.id} className="shadow-sm" data-testid={`card-agent-${a.id}`}>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 dark:text-white truncate">{a.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">{a.login_username}</p>
                </div>
                {a.is_active ? (
                  <span className="shrink-0 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium dark:bg-emerald-900/30 dark:text-emerald-400">Aktif</span>
                ) : (
                  <span className="shrink-0 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium dark:bg-red-900/30 dark:text-red-400">Nonaktif</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-slate-500">Cabang</p>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{a.branch_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Kontak</p>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{a.phone || a.whatsapp || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Komisi</p>
                  {a.commission_type === 'percent' ? (
                    <p className="text-blue-600 font-semibold">{a.commission_value}%</p>
                  ) : (
                    <p className="text-emerald-600 font-semibold">{fmtIDR(a.commission_value)}</p>
                  )}
                </div>
                <div>
                  <p className="text-slate-500">Voucher</p>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{a.voucher_sold || 0} / {a.voucher_total || 0}</p>
                </div>
              </div>
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Total Komisi</p>
                  <p className="text-sm font-bold text-emerald-600">{fmtIDR(a.commission_total)}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => navigate(`/app/vouchers?agent_id=${a.id}`)} data-testid={`m-view-vouchers-${a.id}`}>
                    <Ticket className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`m-edit-${a.id}`}>
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleteConfirm(a)} data-testid={`m-delete-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingId ? <Edit className="w-5 h-5 text-blue-500" /> : <Plus className="w-5 h-5 text-amber-500" />}
              {editingId ? 'Edit Agen Voucher' : 'Tambah Agen Voucher'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Data Agen */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <Users className="w-4 h-4" /> Data Agen
              </p>
              <div>
                <Label>Nama Agen <span className="text-red-500">*</span></Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" placeholder="Contoh: Ahmad" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nomor HP</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="081234567890" />
                </div>
                <div>
                  <Label>WhatsApp</Label>
                  <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="628234567890" />
                </div>
              </div>
              <div>
                <Label>Alamat</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Opsional" />
              </div>
              <div className="rounded-lg border border-slate-300 dark:border-slate-600 p-3 bg-slate-50 dark:bg-slate-800/50 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <Label className="text-slate-700 dark:text-slate-200">Titik Koordinat (Tikor)</Label>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {form.latitude ? `${form.latitude}, ${form.longitude}` : 'Belum diambil'}
                    </div>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={fetchGps} disabled={gpsFetching} data-testid="agent-gps-btn">
                    {gpsFetching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MapPin className="w-4 h-4 mr-1" />}
                    Ambil Lokasi
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px] text-slate-500 dark:text-slate-400">Latitude</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={form.latitude}
                      onChange={(e) => setForm({ ...form, latitude: e.target.value.replace(/[^0-9.\-]/g, '') })}
                      placeholder="-6.4025"
                      data-testid="agent-lat-input"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500 dark:text-slate-400">Longitude</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={form.longitude}
                      onChange={(e) => setForm({ ...form, longitude: e.target.value.replace(/[^0-9.\-]/g, '') })}
                      placeholder="106.8163"
                      data-testid="agent-lng-input"
                      className="text-sm"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  Opsional. Bisa isi otomatis via tombol "Ambil Lokasi" (GPS), atau tempel manual dari Google Maps
                  (klik-kanan di titik → salin koordinat).
                </p>
              </div>
            </div>

            {/* Komisi */}
            <div className="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <Wallet className="w-4 h-4" /> Komisi
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Tipe Komisi <span className="text-red-500">*</span></Label>
                  <Select value={form.commission_type} onValueChange={(v) => setForm({ ...form, commission_type: v })}>
                    <SelectTrigger data-testid="form-commission-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Persentase (%)</SelectItem>
                      <SelectItem value="nominal">Nominal (Rp)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nilai Komisi <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={form.commission_value}
                      onChange={(e) => setForm({ ...form, commission_value: parseFloat(e.target.value) || 0 })}
                      className="pr-10"
                      data-testid="form-commission-value"
                      min="0"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-medium">
                      {form.commission_type === 'percent' ? '%' : 'Rp'}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 p-2 rounded">
                {form.commission_type === 'percent'
                  ? `Contoh: voucher Rp10.000 → agen dapat Rp${((10000 * form.commission_value) / 100).toLocaleString('id-ID')} per voucher terjual`
                  : `Fixed komisi Rp${Number(form.commission_value).toLocaleString('id-ID')} per voucher terjual (tidak tergantung harga voucher)`}
              </p>
            </div>

            {/* Akun Login */}
            <div className="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <KeyRound className="w-4 h-4" /> Akun Login
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Username {!editingId && <span className="text-red-500">*</span>}</Label>
                  <Input
                    value={form.login_username}
                    onChange={(e) => setForm({ ...form, login_username: e.target.value })}
                    disabled={!!editingId}
                    placeholder="agen_ahmad"
                    className={editingId ? 'bg-slate-100 dark:bg-slate-800' : ''}
                    data-testid="form-login-username"
                  />
                  {editingId && <p className="text-xs text-slate-500 mt-1">Username tidak dapat diubah</p>}
                </div>
                <div>
                  <Label>Password {editingId ? '(kosongkan bila tidak reset)' : <span className="text-red-500">*</span>}</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={form.login_password}
                      onChange={(e) => setForm({ ...form, login_password: e.target.value })}
                      className="pr-10"
                      placeholder={editingId ? '••••••' : 'min 6 karakter'}
                      data-testid="form-login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label className="cursor-pointer">Agen Aktif (bisa login &amp; di-assign voucher)</Label>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="submit-agent-btn"
            >
              {saving ? 'Menyimpan...' : (editingId ? 'Simpan Perubahan' : 'Buat Agen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) { setDeleteConfirm(null); setCascadeDelete(false); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" /> Konfirmasi Hapus
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-slate-700 dark:text-slate-200">
              Yakin menghapus agen <span className="font-semibold">{deleteConfirm?.name}</span>?
            </p>
            {(deleteConfirm?.voucher_total > 0) && (
              <>
                <div className="text-xs bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 rounded">
                  <p className="font-semibold text-blue-800 dark:text-blue-300 mb-1">
                    Agen ini memiliki {deleteConfirm.voucher_total} voucher.
                  </p>
                  <p className="text-blue-700 dark:text-blue-400">
                    Tanpa cascade: agen akan di-nonaktifkan (soft delete) - riwayat komisi tersimpan.
                  </p>
                </div>
                <label className="flex items-start gap-2 cursor-pointer p-2 border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded" data-testid="cascade-delete-toggle">
                  <input
                    type="checkbox"
                    checked={cascadeDelete}
                    onChange={(e) => setCascadeDelete(e.target.checked)}
                    className="mt-1 accent-red-600"
                  />
                  <div className="text-xs">
                    <div className="font-semibold text-red-700 dark:text-red-400">
                      Hapus permanen agen + {deleteConfirm.voucher_total} voucher terkait
                    </div>
                    <div className="text-red-600 dark:text-red-300 mt-1">
                      Voucher akan dihapus, session aktif di MikroTik akan di-kick, dan riwayat komisi ikut terhapus. Aksi ini tidak bisa di-undo.
                    </div>
                  </div>
                </label>
              </>
            )}
            {!(deleteConfirm?.voucher_total > 0) && (
              <p className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 p-2 rounded">
                Agen belum punya voucher - akan langsung dihapus permanen.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDeleteConfirm(null); setCascadeDelete(false); }}>Batal</Button>
            <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={doDelete} data-testid="confirm-delete-btn">
              {cascadeDelete && deleteConfirm?.voucher_total > 0 ? `Hapus + ${deleteConfirm.voucher_total} Voucher` : 'Hapus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
